import * as React from "react";
import { useAccount, useConnect, useDisconnect, useSwitchChain } from "wagmi";
import { polygonAmoy } from "wagmi/chains";

function short(address?: string) {
  if (!address) return "";
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function getConnectorLabel(id: string, name: string) {
  if (id === "walletConnect") return "Connect with phone";
  if (id === "io.metamask" || id === "metaMask" || id === "injected") return "MetaMask";
  if (id === "coinbaseWalletSDK") return "Coinbase Wallet";
  return name;
}

function getConnectorDescription(id: string) {
  if (id === "walletConnect") return "Use MetaMask, Trust Wallet, Coinbase Wallet, and more";
  if (id === "io.metamask" || id === "metaMask" || id === "injected") {
    return "Best for desktop browser extension";
  }
  if (id === "coinbaseWalletSDK") return "Use the Coinbase Wallet app or extension";
  return "";
}

function getConnectorPriority(id: string) {
  if (id === "walletConnect") return 0;
  if (id === "io.metamask" || id === "metaMask" || id === "injected") return 1;
  if (id === "coinbaseWalletSDK") return 2;
  return 10;
}

export default function ConnectWalletButton() {
  const { address, isConnected, chain } = useAccount();
  const { disconnect } = useDisconnect();
  const { connect, connectors, isPending, error, pendingConnector } = useConnect();
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();

  const [message, setMessage] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const wrongNetwork = isConnected && chain?.id !== polygonAmoy.id;

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleConnectors = React.useMemo(() => {
    const deduped = new Map<string, (typeof connectors)[number]>();

    for (const connector of connectors) {
      const label = getConnectorLabel(connector.id, connector.name);
      if (!deduped.has(label)) {
        deduped.set(label, connector);
      }
    }

    return Array.from(deduped.values()).sort((a, b) => {
      return getConnectorPriority(a.id) - getConnectorPriority(b.id);
    });
  }, [connectors]);

  async function handleConnect(connectorId: string) {
    setMessage("");
    setOpen(false);

    const connector = connectors.find((c) => c.id === connectorId);

    if (!connector) {
      setMessage(`Connector "${connectorId}" not found.`);
      return;
    }

    try {
      connect({ connector });
    } catch (e: any) {
      setMessage(e?.shortMessage || e?.message || "Wallet connection failed.");
    }
  }

  async function handleSwitchToAmoy() {
    setMessage("");

    try {
      await switchChainAsync({ chainId: polygonAmoy.id });
    } catch (e: any) {
      setMessage(
        e?.shortMessage ||
          e?.message ||
          "Failed to switch network to Polygon Amoy."
      );
    }
  }

  if (isConnected) {
    return (
      <div className="flex flex-col items-end gap-2">
        <div
          className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium"
          title={address}
        >
          <span>{short(address)}</span>
          <span className="opacity-70">{chain?.name ? `• ${chain.name}` : ""}</span>
        </div>

        {wrongNetwork && (
          <button
            type="button"
            onClick={handleSwitchToAmoy}
            disabled={isSwitching}
            className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {isSwitching ? "Switching..." : "Switch to Polygon Amoy"}
          </button>
        )}

        <button
          type="button"
          onClick={() => disconnect()}
          className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium"
        >
          Disconnect
        </button>

        {message && (
          <div className="max-w-[320px] text-right text-[11px] text-red-600">
            {message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative flex flex-col items-end gap-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex min-w-[180px] items-center justify-center rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-medium text-white hover:opacity-95"
      >
        Connect Wallet
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 min-w-[280px] overflow-hidden rounded-2xl border bg-white shadow-xl">
          <div className="border-b px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
            Choose wallet
          </div>

          {visibleConnectors.length === 0 ? (
            <div className="px-4 py-3 text-sm text-red-600">
              No wallet connectors found.
            </div>
          ) : (
            visibleConnectors.map((connector) => {
              const pending = isPending && pendingConnector?.id === connector.id;
              const label = getConnectorLabel(connector.id, connector.name);
              const description = getConnectorDescription(connector.id);

              return (
                <button
                  key={connector.uid}
                  type="button"
                  disabled={isPending}
                  onClick={() => handleConnect(connector.id)}
                  className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-50"
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="text-sm font-medium text-slate-900">{label}</span>
                    <span className="text-xs text-slate-400">
                      {pending ? "Connecting..." : ""}
                    </span>
                  </div>
                  {description && (
                    <span className="text-xs text-slate-500">{description}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      <div className="max-w-[320px] text-right text-[11px] text-muted-foreground">
        Best for mobile: <span className="font-medium">Connect with phone</span>
      </div>

      {(message || error) && (
        <div className="max-w-[320px] text-right text-[11px] text-red-600">
          {message || error?.shortMessage || error?.message}
        </div>
      )}
    </div>
  );
}
